import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import ReactDOM, { findDOMNode } from 'react-dom';
import classnames from 'classnames';
import { autobind } from 'core-decorators';
import getChildPosition from '../get-child-position';
import noop from '../noop';
import { addTapEventListener } from '../tap-events';
import { addClass, removeClass, position, closest } from '../dom';

const HIDE_TIMEOUT = 100;

class Tooltip extends React.Component {
  static displayName = 'Tooltip';
  static propTypes = {
    children: PropTypes.element.isRequired,
    className: PropTypes.string,
    visible: PropTypes.bool,
    alignElement: PropTypes.func,
    alignPositionType: PropTypes.oneOf(['absolute', 'relative']),
    boundariesElement: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    hBoundariesElement: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    vBoundariesElement: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    hasScreenBoundries: PropTypes.bool,
    fullWidth: PropTypes.bool,
    horizontalAlign: PropTypes.string,
    direction: PropTypes.string,
    onShow: PropTypes.func,
    onHide: PropTypes.func,
    caret: PropTypes.bool,
    hoverIntent: PropTypes.bool,
    hideOnClick: PropTypes.bool,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    renderOutsideContainer: PropTypes.bool,
    squeezeBelow: PropTypes.bool
  };
  static defaultProps = {
    alignPositionType: 'relative',
    fullWidth: false,
    direction: 'bottom',
    onShow: noop,
    onHide: noop,
    caret: false,
    hoverIntent: false,
    hideOnClick: true,
    hasScreenBoundries: false,
    onMouseEnter: noop,
    onMouseLeave: noop,
    renderOutsideContainer: false,
    squeezeBelow: false
  };
  constructor(props) {
    super(props);
    this.state = { visible: !!props.visible, isObserving: false };
    /**** ENABLE THIS ONLY WHEN RELEVANT EXCEPTION OCCURS - PLEASE NOTIFY NAORYE
		this._isMounted = false;
        ****/
    this.handleWindowClicked = this.handleWindowClicked.bind(this);
    this.hoverTimeout = null;
    this.resizeObserver = null;
    this.resizeElement = createRef();
  }
  UNSAFE_componentWillReceiveProps(props) {
    if (props.hasOwnProperty('visible') && props.visible !== this.state.visible) {
      this.setState({
        visible: props.visible
      });
    }
  }
  componentDidMount() {
    this.handleComponentUpdate(this.props, this.state);
  }
  componentWillUnmount() {
    this.removeEvents();
    this.resizeObserver?.disconnect();
  }
  componentDidUpdate(prevProps, prevState) {
    const { isObserving, visible } = this.state;
    if (!isObserving && visible) {
      this.observeForResize();
    }
    this.handleComponentUpdate(prevProps, prevState);
  }
  observeForResize() {
    this.resizeObserver = new ResizeObserver(entries => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        if (this.state.visible) this.positionTooltip();
        this.resizeObserver?.observe(this.resizeElement?.current);
      });
    });
  }
  addEvents() {
    if (this.props.hideOnClick) {
      this.disposeHandleWindowClicked = addTapEventListener(document, this.handleWindowClicked); //document.addEventListener('click', this.handleWindowClicked);
      document.addEventListener('hostClick', this.handleWindowClicked);
    }
  }
  removeEvents() {
    if (this.props.hideOnClick) {
      //document.removeEventListener('click', this.handleWindowClicked);
      if (this.disposeHandleWindowClicked) {
        this.disposeHandleWindowClicked();
      }
      document.removeEventListener('hostClick', this.handleWindowClicked);
    }
  }
  handleComponentUpdate(prevProps, prevState) {
    if (this.state.visible && !prevState.visible) {
      this.positionTooltip();

      this.props.onShow();
    } else if (!this.state.visible && prevState.visible) {
      this.props.onHide();
    }
  }
  positionTooltip() {
    /* The following is used instead of maintaining _isMounted - PLEASE NOTIFY NAORYE UPON REMOVAL  */
    if (!this.state.visible) {
      return;
    }
    var {
      alignElement,
      boundariesElement,
      hBoundariesElement,
      vBoundariesElement,
      hasScreenBoundries,
      horizontalAlign,
      direction,
      fullWidth,
      alignPositionType,
      renderOutsideContainer,
      squeezeBelow
    } = this.props;
    let squeezeTooltip;
    var element = findDOMNode(this);
    if (alignElement && typeof alignElement === 'function') {
      alignElement = findDOMNode(alignElement());
      boundariesElement = resolveElement(alignElement, boundariesElement);
      hBoundariesElement = resolveElement(alignElement, hBoundariesElement);
      vBoundariesElement = resolveElement(alignElement, vBoundariesElement);
      squeezeTooltip = squeezeBelow ? this.hasSpaceBelow(alignElement) : false;

      var { canFitHorizontally, position, vertical, horizontal } = getChildPosition(
        alignElement,
        element,
        {
          alignPositionType,
          horizontalAlign,
          spacePosition: direction,
          boundariesElement,
          hBoundariesElement,
          vBoundariesElement,
          fullWidth,
          hasScreenBoundries,
          renderOutsideContainer,
          forcedBelow: squeezeTooltip
        }
      );
      vertical.below = squeezeTooltip ? true : vertical.below; // Fix caret
      // After position the element in the available vertical direction (user can ask for top but maybe there
      // isn't enough space), we add a marker class

      if (vertical.below) {
        // Bottom positioned
        addClass(element, 'below');
        removeClass(element, 'above');
      } else if (vertical.above) {
        // Top positioned
        addClass(element, 'above');
        removeClass(element, 'below');
      }

      if (horizontal.left) {
        // left positioned
        addClass(element, 'align-left');
        removeClass(element, 'align-right');
      } else if (horizontal.right) {
        // right positioned
        addClass(element, 'align-right');
        removeClass(element, 'align-left');
      }

      if (canFitHorizontally) {
        removeClass(element, 'multiline');
      } else {
        addClass(element, 'multiline');
      }

      Object.assign(element.style, position);
    }

    this.positionCaret();
  }

  hasSpaceBelow(alignElement) {
    // Get child position is more stricter than this function - meaning tooltip going up while there is still space below
    const alignElementHeight = alignElement.getBoundingClientRect().top;
    const windowHeight = window.document.body.getBoundingClientRect().height;
    if (windowHeight - alignElementHeight > 400) {
      return true;
    }

    return false;
  }

  positionCaret() {
    if (this.props.caret) {
      var alignElement = findDOMNode(this.props.alignElement());
      var alignRect = alignElement.getBoundingClientRect();
      var alignWidth = alignRect.width;

      var caret = findDOMNode(this.refs.caret);
      var caretRect = caret.getBoundingClientRect();
      var caretLeft = position(caret).left;
      var caretWidth = caretRect.width;

      var leftDistance = caretRect.left + caretWidth / 2 - (alignRect.left + alignWidth / 2);
      if (leftDistance > 0) {
        caret.style.left = caretLeft - Math.abs(leftDistance);
      } else {
        caret.style.left = caretLeft + Math.abs(leftDistance);
      }
    }
  }

  toggle() {
    if (this.state.visible) {
      this.hide();
    } else {
      this.show();
    }
  }
  show() {
    if (this.props.hoverIntent) {
      clearTimeout(this.hoverTimeout);
    }
    if (this.state.visible) {
      return;
    }

    this.setState({ visible: true });
    setTimeout(() => this.addEvents(), 0);
  }
  hide() {
    if (this.props.hoverIntent) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = setTimeout(this.close.bind(this), HIDE_TIMEOUT);
    } else {
      this.close();
    }
  }
  close() {
    this.removeEvents();
    if (!this.state.visible) {
      return;
    }
    this.setState({ visible: false });
  }
  @autobind
  onMouseEnter() {
    if (this.props.hoverIntent) {
      clearTimeout(this.hoverTimeout);
    }
    this.props.onMouseEnter();
  }
  @autobind
  onMouseLeave() {
    if (this.props.hoverIntent) {
      this.hide();
    }
    this.props.onMouseLeave();
  }
  handleWindowClicked(event) {
    if (!this.state.visible) {
      return;
    }

    var element = findDOMNode(this);
    if (element && !element.contains(event.target) && document.body.contains(event.target)) {
      this.close();
    }
  }
  isVisible() {
    return this.state.visible;
  }
  stopPropagation(event) {
    event.stopPropagation();
    event.preventDefault();
  }
  render() {
    var tooltip = null;
    var caret = null;

    var { children, className, renderOutsideContainer } = this.props;

    if (this.props.caret) {
      caret = <div className="caret" ref="caret" />;
    }

    if (this.state.visible) {
      var tooltipClassname = classnames('tooltip', 'open', className);
      tooltip = (
        <div
          ref={this.resizeElement}
          onMouseEnter={this.onMouseEnter}
          onMouseLeave={this.onMouseLeave}
          className={tooltipClassname}
          onClick={this.stopPropagation.bind(this)}
        >
          {children}
          {caret}
        </div>
      );
    }

    if (renderOutsideContainer) {
      return ReactDOM.createPortal(tooltip, document.querySelector('#app'));
    } else {
      return tooltip;
    }
  }
}

function resolveElement(alignElement, funcOrSelector) {
  var element;
  if (funcOrSelector) {
    switch (typeof funcOrSelector) {
      case 'function':
        element = findDOMNode(funcOrSelector());
        break;
      case 'string':
        element = closest(alignElement, funcOrSelector);
        break;
    }
  }
  return element;
}

export default Tooltip;
